import { type EnvironmentId, type ScopedThreadRef } from "@cmmd-forge/contracts";
import { type RefObject, useEffect } from "react";
import { type DraftId } from "../../draftId";
import { type ChatComposerHandle } from "./ChatComposerHandle";
import { type Thread } from "../../types";

export function usePendingAutoSubmit(input: {
  readonly pendingAutoSubmit: boolean;
  readonly activeThread: Thread | undefined;
  readonly activeProjectExists: boolean;
  readonly environmentId: EnvironmentId;
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly composerDraftTarget: ScopedThreadRef | DraftId;
  readonly composerReady: boolean;
  readonly composerDisabledReason: string | false | null;
  readonly composerRef: RefObject<ChatComposerHandle | null>;
  readonly setPendingAutoSubmit: (
    target: ScopedThreadRef | DraftId,
    pendingAutoSubmit: boolean,
  ) => void;
  readonly onSend: () => void;
}): void {
  useEffect(() => {
    if (!input.pendingAutoSubmit) return;
    if (!input.activeThread || input.isSendBusy || input.isConnecting) return;
    if (!input.activeProjectExists) return;
    if (!input.composerReady || input.composerDisabledReason !== null) return;
    if (!input.composerRef.current) return;
    input.setPendingAutoSubmit(input.composerDraftTarget, false);
    input.onSend();
    // `onSend` changes as render state changes. The one-shot flag is the trigger,
    // and this effect intentionally consumes the current closure when the flag flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    input.pendingAutoSubmit,
    input.activeThread,
    input.activeProjectExists,
    input.environmentId,
    input.isSendBusy,
    input.isConnecting,
    input.composerDraftTarget,
    input.composerReady,
    input.composerDisabledReason,
    input.setPendingAutoSubmit,
  ]);
}
